import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { LowMedicationWidgetStore } from './low-medication-widget.store';
import { clientChipField, serviceField } from '../../../../../shared/components/kit/table/table.fields';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { getMedicationNameFrag, medicationFrag } from './low-medication-widget.query';
import { FolderMedicationGridItemObject } from '@it2go/types';
import { TableComponent } from '../../../../../shared/components/kit/table/table.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import {
    MedicationInfoComponent,
} from '../../../../../shared/components/kit/medication-info/medication-info.component';
import { countDaysDiff, CountDaysDiffPipe } from '../../../../../shared/pipes/count-days-diff.pipe';
import { formatDaysDiff, FormatDaysDiffPipe } from '../../../../../shared/pipes/format-days-diff.pipe';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';

@Component({
    selector: 'app-low-medication-widget',
    templateUrl: './low-medication-widget.component.html',
    styleUrl: './low-medication-widget.component.scss',
    providers: [LowMedicationWidgetStore],
    imports: [
        TableComponent,
        PrimeTemplate,
        ButtonComponent,
        MedicationInfoComponent,
        CountDaysDiffPipe,
        FormatDaysDiffPipe,
    ],
})
export class LowMedicationWidgetComponent {
    protected readonly store = inject(LowMedicationWidgetStore);

    items = input<{ serviceId: string, widget: UserWidgetSetting }[]>([]);

    protected columns = computed(() => {
        const showServiceInfo = [...new Set(this.items().map(x => x.serviceId))].length > 1;

        let cols = [
            clientChipField('client', 'folder.person', false),
            {
                name: 'stock',
                dataKey: null,
                exportAs: (item: any) => formatDaysDiff(countDaysDiff(item)),
            },
            {
                name: 'medicine',
                dataKey: null,
                exportAs: async (item: any) => {
                    const res = await this.store.api.getMedications(
                        FilterBuilder.singleItem({ code: item.medicationCode }),
                        getMedicationNameFrag,
                    );
                    const name = `${res?.items?.[0]?.name || ''} ${res?.items?.[0]?.nameAddition || ''}`.trim();

                    return `${item?.customMedication ? `${item.customMedication}, ` : ''}${name}`;
                },
            },
        ];

        if (showServiceInfo) {
            cols = [
                ...cols.slice(0, 1),
                serviceField('service', 'serviceId'),
                ...cols.slice(1),
            ];
        }

        return cols;
    });

    protected data = signal<FolderMedicationGridItemObject[]>([]);

    constructor() {
        effect(async () => {
            if (!this.items().length) {
                return;
            }

            const promises = this.items().map(async (item) => {
                const packageToDate = new Date();
                packageToDate.setDate(packageToDate.getDate() + (item.widget.days));

                return this.store.api.getFolderMedications({
                    filter: [
                        [{ column: 'serviceId', operator: 'EQ', value: [item.serviceId] }],
                        [{
                            column: 'packageTo',
                            operator: 'LTE',
                            value: [packageToDate.toISOString()],
                        }],
                        [{
                            column: 'to',
                            operator: 'GTE',
                            value: [new Date().toISOString()],
                        }],
                        [{ column: 'active', operator: 'EQ', value: ['true'] }],
                    ],
                }, medicationFrag);
            });

            const data = await Promise.all(promises);
            this.data.set(data.flatMap(obj => (obj?.items || []) as FolderMedicationGridItemObject[]));
        });
    }
}
